import { generatePath } from '@/services/router/generate-path';
import { useMemo } from 'react';
import {
  Stack,
  Text,
  Badge,
  Panel,
  PanelHeader,
  PanelTitle,
  PanelSection,
  InlineStack,
  StatusIndicator,
  Link,
  Drawer,
  DrawerBody,
  DrawerInset,
  DataTable,
  Box,
} from '@customerio/pluma-components/react';
import { createColumnHelper } from '@tanstack/table-core';
import { useAnalytics } from '@/services/analytics';
import {
  SEVERITY_COLOR,
  useEnvHealth,
  useWorkspaceHealthSimulation,
  DATA_ALERT_SEVERITY_RANK,
} from '@/features/workspace-health';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { useTableDrawer } from '../hooks';
import * as styles from './index.css';

// Labels from LogTypeNames in apps/ember/app/utils/activity-log-types.ts
const FAILED_EVENT_LABELS: Record<string, string> = {
  failed_action: 'Failed to Send Action',
  failed_attribute_change: 'Failed Attribute Change',
  failed_batch_update: 'Failed Batch Update',
  failed_email: 'Failed to Send Email',
  failed_event: 'Failed Event',
  failed_in_app: 'Failed to Send In-App Message',
  failed_line: 'Failed to Send LINE Message',
  failed_llm_action: 'Failed LLM',
  failed_object_journeys: 'Failed Journeys for Object/Relationship Campaign',
  failed_push: 'Failed to Send Push Notification',
  failed_query_collection: 'Failed Collection Query',
  failed_slack: 'Failed to Send Slack Message',
  failed_twilio: 'Failed to Send SMS',
  failed_webhook: 'Failed to Send Webhook',
};

const DATA_ACTION_TYPES = new Set(['processing_details', 'imports', 'failed_events', 'tasks']);

interface FailedEventEntry {
  key: string;
  label: string;
  count: number;
}

const columnHelper = createColumnHelper<FailedEventEntry>();

export function DataAlertsPanel() {
  const workspaceId = getWorkspaceIdFromCurrentUrl() ?? 0;
  const { track } = useAnalytics();
  const { simulate } = useWorkspaceHealthSimulation();
  const healthQuery = useEnvHealth(workspaceId, simulate);
  const data = healthQuery.data;
  const alerts = (data?.alerts ?? [])
    .filter((a) => DATA_ACTION_TYPES.has(a.action_type))
    .sort((a, b) => DATA_ALERT_SEVERITY_RANK[a.severity] - DATA_ALERT_SEVERITY_RANK[b.severity]);
  const hasAlerts = alerts.length > 0;
  const { isDrawerOpen, setIsDrawerOpen } = useTableDrawer('data-alerts');

  const failedEvents = data?.stats.failed_events_today;

  const failedEntries = useMemo(() => {
    if (failedEvents == null) return [];
    return Object.entries(failedEvents)
      .filter(([, count]) => count > 0)
      .map(([key, count]) => ({
        key,
        label: FAILED_EVENT_LABELS[key] ?? key,
        count,
      }))
      .sort((a, b) => b.count - a.count);
  }, [failedEvents]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('label', {
        header: 'Type',
        cell: (info) => {
          const typeKey = info.row.original.key;
          return (
            <Link
              href={generatePath('/workspaces/:workspaceId/journeys/activity_logs', { workspaceId }, { type: typeKey })}
              variant="secondary"
              isUnderlined={false}
            >
              <Text size="s">{info.getValue()}</Text>
            </Link>
          );
        },
      }),
      columnHelper.accessor('count', {
        header: 'Count',
        cell: (info) => <Badge>{info.getValue().toLocaleString()}</Badge>,
      }),
    ],
    [workspaceId],
  );

  return (
    <>
      <Panel>
        <PanelHeader>
          <PanelTitle>
            <InlineStack gap="050" alignBlock="center">
              Status updates
              {hasAlerts ? <Badge isEmphasized>{alerts.length}</Badge> : null}
            </InlineStack>
          </PanelTitle>
        </PanelHeader>
        <PanelSection withPadding={false}>
          {hasAlerts ? (
            <Stack px="300">
              {alerts.map((alert, index) => (
                <Stack
                  key={index}
                  gap="050"
                  pt={index === 0 ? undefined : '200'}
                  pb={index === alerts.length - 1 ? '300' : '200'}
                  className={styles.categoryGroup}
                >
                  <InlineStack gap="100" alignBlock="center">
                    <StatusIndicator color={SEVERITY_COLOR[alert.severity]} />
                    <Text size="s" weight="semibold">
                      {alert.title}
                    </Text>
                  </InlineStack>
                  <Stack gap="100">
                    <Text size="xs" color="subtle">
                      {alert.message}
                    </Text>
                    {alert.action_type === 'failed_events' ? (
                      <Box>
                        <Link
                          onClick={() => {
                            void track('workspaceHealth', 'drawerOpened', { Drawer: 'data_alerts' });
                            setIsDrawerOpen('data-alerts');
                          }}
                          variant="secondary"
                        >
                          {alert.action_label}
                        </Link>
                      </Box>
                    ) : null}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          ) : (
            <InlineStack gap="100" alignBlock="center" px="300" pb="300">
              <Text size="s" color="subtle">
                All clear, no data issues
              </Text>
            </InlineStack>
          )}
        </PanelSection>
      </Panel>

      <Drawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(null)} title="Failed activities today">
        <DrawerBody>
          <Text size="s" color="subtle">
            All failed activities recorded since midnight UTC. Click a type to view details in the activity log.
          </Text>
          <DrawerInset pt="200" px="050">
            <DataTable data={failedEntries} columns={columns} caption="Failed activities" withSorting={true} />
          </DrawerInset>
        </DrawerBody>
      </Drawer>
    </>
  );
}
